import { MailCodeResponse } from '@thesavingsgroup/apex-api/dto/MailCodeResponse';
import { ApplicationType } from '@thesavingsgroup/enums/ApplicationType';
import { isInEnum } from '@thesavingsgroup/utils/isInEnum';
import { isNonEmptyString } from '@thesavingsgroup/utils/isNonEmptyString';
import { isNumberGreaterThanZero } from '@thesavingsgroup/utils/isNumberGreaterThanZero';
import i18n from 'i18next';
import capitalize from 'lodash/capitalize';
import lodashGet from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import toString from 'lodash/toString';
import { StateCreator } from 'zustand';

import { mailCodeOfferStatusRoutes } from './constants/mailCodeOfferStatusRoutes';
import { mailCodeRequiredOfferFields } from './constants/mailCodeRequiredOfferFields';
import { MailCodeOfferStatus } from './enums/MailCodeOfferStatus';
import { MailCodeOfferType } from './enums/MailCodeOfferType';
import { toMissingOfferFields } from './functions/toMissingOfferFields';
import { toOfferDisplayValues } from './functions/toOfferDisplayValues';
import { toOfferStatus } from './functions/toOfferStatus';
import { MailCodeDataApplicantPhysicalAddress } from './types/MailCodeDataApplicantPhysicalAddress';
import { MailCodeOfferDisplayValues } from './types/MailCodeOfferDisplayValues';
import { MailCodeOfferField } from './types/MailCodeOfferField';
import { MailCodeState } from './types/MailCodeState';
import { MailCodeStateDependencies } from './types/MailCodeStateDependencies';
import { SetMailCodeActivationStateParams } from './types/SetMailCodeActivationStateParams';

export const keysToPersist = [
  'firstName',
  'lastName',
  'mailCode',
  'mailCodeData',
  'primaryEmailAddress',
  'primaryPhoneNumber',
  'mailCodeUniqueIdentifierData',
];

export const mailCodeStore =
  ({
    useAnalyticStore,
    useApiStore,
    useAttributionStore,
    useParameterStore,
  }: MailCodeStateDependencies): StateCreator<MailCodeState> =>
  (set, get) => ({
    fetchMailCodeDataFailedMessage: null,
    fetchMailCodeDataSuccess: false,
    firstName: '',
    isMailCodeInvalidSourceError: false,
    isMailCodeNotFoundError: false,
    lastName: '',
    mailCode: null,
    mailCodeData: null,
    mailCodeUniqueIdentifierData: {},
    primaryEmailAddress: '',
    primaryPhoneNumber: '',

    computed: {
      get activationSubtitleText(): string {
        const { applicationType } = useParameterStore.getState();

        const isDefaultSubtitle = !isInEnum(applicationType, ApplicationType);

        return isDefaultSubtitle
          ? i18n.t('MailCodeActivationPage.subtitle.default')
          : i18n.t(`MailCodeActivationPage.subtitle.${applicationType}`);
      },
      get activationTitleText(): string {
        const { applicationType } = useParameterStore.getState();

        const isDefaultTitle = !isInEnum(applicationType, ApplicationType);

        return isDefaultTitle
          ? i18n.t('MailCodeActivationPage.title.default')
          : i18n.t(`MailCodeActivationPage.title.${applicationType}`);
      },
      get applicantFirstName(): string | null {
        const { mailCodeData } = get();

        if (!mailCodeData) {
          return null;
        }

        return capitalize(mailCodeData.payload.applicant.firstName);
      },
      get applicantLastName(): string | null {
        const { mailCodeData } = get();

        if (!mailCodeData) {
          return null;
        }

        return capitalize(mailCodeData.payload.applicant.lastName);
      },
      get applicantPhysicalAddress(): MailCodeDataApplicantPhysicalAddress | null {
        const { mailCodeData } = get();

        if (!mailCodeData) {
          return null;
        }

        return {
          physicalAddressCity: mailCodeData.payload.applicant.city,
          physicalAddressLineOne: mailCodeData.payload.applicant.address,
          physicalAddressPostalCode: mailCodeData.payload.applicant.zipCode,
          physicalAddressProvince: mailCodeData.payload.applicant.state,
        };
      },
      get hasValidApplicantPhysicalAddress(): boolean {
        const {
          computed: { applicantPhysicalAddress },
        } = get();

        if (!applicantPhysicalAddress) {
          return false;
        }

        return [
          applicantPhysicalAddress.physicalAddressCity,
          applicantPhysicalAddress.physicalAddressLineOne,
          applicantPhysicalAddress.physicalAddressPostalCode,
          applicantPhysicalAddress.physicalAddressProvince,
        ].every(isNonEmptyString);
      },
      get isPrimaryPhoneNumberEmpty(): boolean {
        const { primaryPhoneNumber } = get();

        return isEmpty(primaryPhoneNumber);
      },
      get missingOfferFields(): MailCodeOfferField[] {
        const {
          computed: { offerType },
          mailCodeData,
        } = get();

        return toMissingOfferFields(
          mailCodeRequiredOfferFields[offerType],
          mailCodeData?.payload.mailerCode,
        );
      },
      get offerDisplayValues(): MailCodeOfferDisplayValues {
        const { mailCodeData } = get();

        return toOfferDisplayValues(mailCodeData?.payload.mailerCode);
      },
      get offerStatus(): MailCodeOfferStatus {
        const {
          computed: { missingOfferFields, offerType },
          mailCodeData,
        } = get();

        if (!mailCodeData) {
          return MailCodeOfferStatus.NO_MAIL_CODE;
        }

        return toOfferStatus(
          mailCodeRequiredOfferFields[offerType],
          missingOfferFields,
        );
      },
      get offerStatusRoute(): string {
        const {
          computed: { offerStatus },
        } = get();

        return mailCodeOfferStatusRoutes[offerStatus];
      },
      get offerType(): MailCodeOfferType {
        const { applicationType } = useParameterStore.getState();

        // a missing or unknown applicationType is a Refinance, as in toApplyStateFromMailCode
        return applicationType === ApplicationType.LEASE_BUYOUT ||
          applicationType === ApplicationType.PURCHASE
          ? MailCodeOfferType.RATE
          : MailCodeOfferType.SAVINGS;
      },
      get preApprovalAmountAsNumber(): number {
        const { mailCodeData } = get();

        if (!mailCodeData) {
          return 0;
        }

        return mailCodeData.payload.mailerCode.preApprovalAmount;
      },
      get shouldFetchMailCodeDataOnLoad(): boolean {
        const { fetchMailCodeDataFailedMessage } = get();
        const { mailCode: parameterMailCode } = useParameterStore.getState();

        return (
          // mail code is present in the URL parameters
          isNonEmptyString(parameterMailCode) &&
          // we have not already attempted to fetch mail code data in which it failed
          isEmpty(fetchMailCodeDataFailedMessage)
        );
      },
      get vehiclePayoffAmount(): string | null {
        const { mailCodeData } = get();

        if (!mailCodeData) {
          return null;
        }

        const { payoffAmount } = mailCodeData.payload.vehicle;

        return isNumberGreaterThanZero(payoffAmount)
          ? String(payoffAmount)
          : null;
      },
      get vehicleVin(): string | null {
        const { mailCodeData } = get();

        return mailCodeData?.payload.vehicle.vin || null;
      },
    },
    fetchMailCodeData: (mailCode: string): Promise<void> => {
      const { leadChannelCode } = useParameterStore.getState();
      const { addOrReplaceAttributionsForLeadChannel } =
        useAttributionStore.getState();
      const { sendMailCodeErrorAnalytics, sendMailCodeFoundAnalytics } =
        useAnalyticStore.getState();

      const portalHttpRequest = useApiStore.getState().portalApi();

      set({
        fetchMailCodeDataFailedMessage: null,
        fetchMailCodeDataSuccess: false,
        isMailCodeInvalidSourceError: false,
        isMailCodeNotFoundError: false,
      });

      return portalHttpRequest
        .get<{ data: MailCodeResponse }>(
          `/2.0/mail-code?leadChannelCode=${leadChannelCode}&mailCode=${mailCode}`,
        )
        .then(({ data }): void => {
          sendMailCodeFoundAnalytics();

          addOrReplaceAttributionsForLeadChannel(leadChannelCode, {
            mailCode,
            partnerRecordUniqueIdentifier: mailCode,
          });

          set({ fetchMailCodeDataSuccess: true, mailCodeData: data });
        })
        .catch((error: unknown): void => {
          sendMailCodeErrorAnalytics();

          const status = toString(lodashGet(error as any, 'response.status'));
          const isMailCodeNotFoundError = status === '404';
          const isMailCodeInvalidSourceError = status === '500';

          set({
            fetchMailCodeDataFailedMessage: lodashGet(
              i18n.t('errors.fetchMailCode.statusMessage'),
              status,
              i18n.t('errors.fetchMailCode.defaultMessage'),
            ),
            isMailCodeInvalidSourceError,
            isMailCodeNotFoundError,
          });
        });
    },
    findMailCodeDataByUniqueIdentifier: (
      uniqueIdentifier: string | undefined,
    ): MailCodeResponse | undefined => {
      const { mailCodeUniqueIdentifierData } = get();

      if (
        !uniqueIdentifier ||
        !(uniqueIdentifier in mailCodeUniqueIdentifierData)
      ) {
        return undefined;
      }

      return mailCodeUniqueIdentifierData[uniqueIdentifier];
    },
    resolveMailCodeState: (): void => {
      set((state) => ({
        fetchMailCodeDataFailedMessage: null,
        fetchMailCodeDataSuccess: false,
        firstName: '',
        isMailCodeInvalidSourceError: false,
        isMailCodeNotFoundError: false,
        lastName: '',
        mailCode: null,
        mailCodeData: null,
        primaryEmailAddress: '',
        primaryPhoneNumber: '',

        mailCodeUniqueIdentifierData: {
          ...state.mailCodeUniqueIdentifierData,
          [state.mailCode]: state.mailCodeData,
        },
      }));
    },
    setMailCodeActivationState: (
      params: SetMailCodeActivationStateParams,
    ): void => {
      const {
        firstNameValue,
        lastNameValue,
        primaryEmailAddressValue,
        primaryPhoneNumberValue,
      } = params;

      set({
        firstName: firstNameValue,
        lastName: lastNameValue,
        primaryEmailAddress: primaryEmailAddressValue,
        primaryPhoneNumber: primaryPhoneNumberValue,
      });
    },
  });
